import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  api,
  type Alias,
  type AuditEntry,
  type DashboardCounters,
  type DashboardQueueSummary,
  type DashboardRecentAudit,
  type DashboardStorage,
  type DashboardSystem,
  type DkimKey,
  type DnsReport,
  type Domain,
  type Mailbox,
  type MailboxUsage,
  type QueueEntry,
  type SieveScript,
} from "./client";

export function useMe() {
  return useQuery({
    queryKey: ["me"],
    queryFn: () => api.get<{ email: string }>("/me"),
    retry: false,
  });
}

export function useMailboxes() {
  return useQuery({
    queryKey: ["mailboxes"],
    queryFn: () => api.get<{ mailboxes: Mailbox[] }>("/mailboxes"),
  });
}

export function useMailboxUsage(address: string | null) {
  return useQuery({
    queryKey: ["mailbox-usage", address],
    queryFn: () =>
      api.get<MailboxUsage>(`/mailboxes/${encodeURIComponent(address!)}/usage`),
    enabled: !!address,
    staleTime: 15_000,
  });
}

export function useCreateMailbox() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { address: string; password: string; quotaBytes?: number }) =>
      api.post<Mailbox>("/mailboxes", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mailboxes"] }),
  });
}

export function useUpdateMailbox() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      address,
      ...body
    }: {
      address: string;
      active?: boolean;
      quotaBytes?: number;
      forwardTo?: string | null;
      forwardKeepCopy?: boolean;
    }) => api.patch<{ ok: true }>(`/mailboxes/${encodeURIComponent(address)}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mailboxes"] }),
  });
}

export function useResetMailboxPassword() {
  return useMutation({
    mutationFn: ({ address, newPassword }: { address: string; newPassword: string }) =>
      api.post<{ ok: true }>(
        `/mailboxes/${encodeURIComponent(address)}/password`,
        { newPassword },
      ),
  });
}

export function useDeleteMailbox() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (address: string) =>
      api.delete<{ ok: true }>(`/mailboxes/${encodeURIComponent(address)}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mailboxes"] }),
  });
}

export function useAliases() {
  return useQuery({
    queryKey: ["aliases"],
    queryFn: () => api.get<{ aliases: Alias[] }>("/aliases"),
  });
}

export function useCreateAlias() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { address: string; goto: string }) =>
      api.post<Alias>("/aliases", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["aliases"] }),
  });
}

export function useDeleteAlias() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { address: string; goto: string }) =>
      api.delete<{ ok: true }>("/aliases", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["aliases"] }),
  });
}

export function useDomains() {
  return useQuery({
    queryKey: ["domains"],
    queryFn: () => api.get<{ domains: Domain[] }>("/domains"),
  });
}

export function useCreateDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string }) =>
      api.post<{ name: string; active: boolean }>("/domains", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["domains"] }),
  });
}

export function useDeleteDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      api.delete<{ ok: true; dkimRemoved: number }>(
        `/domains/${encodeURIComponent(name)}`,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["domains"] });
      qc.invalidateQueries({ queryKey: ["dkim"] });
    },
  });
}

export function useAudit(params: { admin?: string; action?: string; target?: string }) {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) search.set(k, v);
  const qs = search.toString();
  return useQuery({
    queryKey: ["audit", params],
    queryFn: () => api.get<{ entries: AuditEntry[] }>(`/audit${qs ? `?${qs}` : ""}`),
  });
}

export function useRequestOtp() {
  return useMutation({
    mutationFn: (email: string) => api.post<{ ok: true }>("/auth/request-otp", { email }),
  });
}

export function useVerifyOtp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; code: string }) =>
      api.post<{ ok: true; email: string }>("/auth/verify-otp", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me"] }),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ ok: true }>("/auth/logout"),
    onSuccess: () => qc.setQueryData(["me"], null),
  });
}

// ---- DKIM -----------------------------------------------------------------

export function useDkim() {
  return useQuery({
    queryKey: ["dkim"],
    queryFn: () => api.get<{ keys: DkimKey[] }>("/dkim"),
  });
}

export function useGenerateDkim() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { domain: string; selector: string }) =>
      api.post<DkimKey & { ok: true }>("/dkim", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["dkim"] }),
  });
}

export function useDeleteDkim() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ domain, selector }: { domain: string; selector: string }) =>
      api.delete<{ ok: true }>(
        `/dkim/${encodeURIComponent(domain)}/${encodeURIComponent(selector)}`,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["dkim"] }),
  });
}

// ---- DNS health -----------------------------------------------------------

export function useDnsHealth(domain: string | null) {
  return useQuery({
    queryKey: ["dns", domain],
    queryFn: () =>
      api.get<DnsReport>(`/dns/${encodeURIComponent(domain!)}/health`),
    enabled: !!domain,
    staleTime: 30_000,
  });
}

// ---- Sieve ----------------------------------------------------------------

export function useSieveList(address: string | null) {
  return useQuery({
    queryKey: ["sieve", address],
    queryFn: () =>
      api.get<{ ok: true; scripts: SieveScript[] }>(
        `/mailboxes/${encodeURIComponent(address!)}/sieve`,
      ),
    enabled: !!address,
  });
}

export function useSieveGet(address: string | null, script: string | null) {
  return useQuery({
    queryKey: ["sieve-script", address, script],
    queryFn: () =>
      api.get<{ ok: true; name: string; body: string }>(
        `/mailboxes/${encodeURIComponent(address!)}/sieve/${encodeURIComponent(script!)}`,
      ),
    enabled: !!address && !!script,
  });
}

export function useSieveSave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      address,
      script,
      body,
      activate,
    }: {
      address: string;
      script: string;
      body: string;
      activate?: boolean;
    }) =>
      api.put<{ ok: true }>(
        `/mailboxes/${encodeURIComponent(address)}/sieve/${encodeURIComponent(script)}`,
        { body, activate },
      ),
    onSuccess: (_d, vars) =>
      qc.invalidateQueries({ queryKey: ["sieve", vars.address] }),
  });
}

export function useSieveActivate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ address, script }: { address: string; script: string }) =>
      api.post<{ ok: true }>(
        `/mailboxes/${encodeURIComponent(address)}/sieve/${encodeURIComponent(script)}/activate`,
      ),
    onSuccess: (_d, vars) =>
      qc.invalidateQueries({ queryKey: ["sieve", vars.address] }),
  });
}

export function useSieveDeactivate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (address: string) =>
      api.post<{ ok: true }>(
        `/mailboxes/${encodeURIComponent(address)}/sieve-deactivate`,
      ),
    onSuccess: (_d, address) =>
      qc.invalidateQueries({ queryKey: ["sieve", address] }),
  });
}

export function useSieveDelete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ address, script }: { address: string; script: string }) =>
      api.delete<{ ok: true }>(
        `/mailboxes/${encodeURIComponent(address)}/sieve/${encodeURIComponent(script)}`,
      ),
    onSuccess: (_d, vars) =>
      qc.invalidateQueries({ queryKey: ["sieve", vars.address] }),
  });
}

export function useSieveVacation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      address,
      ...body
    }: {
      address: string;
      subject: string;
      body: string;
      days: number;
      from?: string;
      start?: string;
      end?: string;
      addresses?: string[];
    }) =>
      api.post<{ ok: true; body: string }>(
        `/mailboxes/${encodeURIComponent(address)}/sieve/vacation`,
        body,
      ),
    onSuccess: (_d, vars) =>
      qc.invalidateQueries({ queryKey: ["sieve", vars.address] }),
  });
}

// ---- Queue ----------------------------------------------------------------

export function useQueue() {
  return useQuery({
    queryKey: ["queue"],
    queryFn: () => api.get<{ ok: true; entries: QueueEntry[] }>("/queue"),
    refetchInterval: 15_000,
  });
}

export function useQueueFlush() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ ok: true }>("/queue/flush"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["queue"] }),
  });
}

export function useQueueAction(verb: "delete" | "hold" | "release") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<{ ok: true }>(`/queue/${id}/${verb}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["queue"] }),
  });
}

// ---- Dashboard ------------------------------------------------------------

export function useDashboardCounters() {
  return useQuery({
    queryKey: ["dashboard-counters"],
    queryFn: () => api.get<DashboardCounters>("/dashboard/counters"),
    staleTime: 30_000,
  });
}

export function useDashboardStorage() {
  return useQuery({
    queryKey: ["dashboard-storage"],
    queryFn: () => api.get<DashboardStorage>("/dashboard/storage"),
    staleTime: 60_000,
  });
}

export function useDashboardQueueSummary() {
  return useQuery({
    queryKey: ["dashboard-queue-summary"],
    queryFn: () => api.get<DashboardQueueSummary>("/dashboard/queue-summary"),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}

export function useDashboardRecentAudit() {
  return useQuery({
    queryKey: ["dashboard-recent-audit"],
    queryFn: () => api.get<DashboardRecentAudit>("/dashboard/recent-audit"),
    staleTime: 30_000,
  });
}

export function useDashboardSystem() {
  return useQuery({
    queryKey: ["dashboard-system"],
    queryFn: () => api.get<DashboardSystem>("/dashboard/system"),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}
